import React, { useState } from 'react';
import {
  Box,
  List,
  ListItem,
  IconButton,
  TextField,
  InputAdornment,
  Typography,
  Tooltip,
} from '@mui/material';
import {
  Add as AddIcon,
  Search as SearchIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Check as CheckIcon,
  Close as CloseIcon,
  ArrowBack as ArrowBackIcon
} from '@mui/icons-material';
import { colors } from '../theme';
import AuthButton from './AuthButton';

const Sidebar = ({
  conversations,
  selectedConversation,
  onConversationSelect,
  onNewChat,
  collapsed = false,
  onRenameConversation,
  onDeleteConversation,
  isMobile = false,
  onCloseMobile
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [hoveredChat, setHoveredChat] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(null);
  const [editingChat, setEditingChat] = useState(null);
  const [editTitle, setEditTitle] = useState('');

  const sidebarWidth = isMobile ? '100%' : (collapsed ? 0 : 320);

  const groupedConversations = conversations.reduce((groups, conv) => {
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const convTime = conv.updatedAt || conv.createdAt || conv.timestamp || now;
    const convDate = new Date(convTime);
    const convDay = new Date(convDate.getFullYear(), convDate.getMonth(), convDate.getDate());
    const diffDays = Math.floor((today - convDay) / (1000 * 60 * 60 * 24));

    let group = '更早';
    if (diffDays <= 0) {
      group = '今天';
    } else if (diffDays === 1) {
      group = '昨天';
    } else if (diffDays <= 7) {
      group = '本周';
    } else if (diffDays <= 30) {
      group = '本月';
    }

    if (!groups[group]) groups[group] = [];
    groups[group].push(conv);
    return groups;
  }, {});

  const filteredConversations = Object.entries(groupedConversations).reduce((acc, [group, chats]) => {
    const filtered = chats.filter(chat =>
      (chat.title && chat.title.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (chat.lastMessage && chat.lastMessage.toLowerCase().includes(searchTerm.toLowerCase()))
    );
    if (filtered.length > 0) acc[group] = filtered;
    return acc;
  }, {});

  const formatTime = (timestamp) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diff = now - date;
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);

    if (minutes < 1) return '刚刚';
    if (minutes < 60) return `${minutes}分钟前`;
    if (hours < 24) return `${hours}小时前`;
    if (days < 7) return `${days}天前`;
    return date.toLocaleDateString();
  };

  const handleDeleteClick = (e, conversation) => {
    e.stopPropagation();
    setShowDeleteConfirm(conversation._id);
  };

  const confirmDelete = async (e, conversationId) => {
    e.stopPropagation();
    if (onDeleteConversation) await onDeleteConversation(conversationId);
    setShowDeleteConfirm(null);
  };

  const cancelDelete = (e) => {
    e.stopPropagation();
    setShowDeleteConfirm(null);
  };

  const handleStartEdit = (e, conversation) => {
    e.stopPropagation();
    setEditingChat(conversation._id);
    setEditTitle(conversation.title);
  };

  const handleConfirmRename = (e, conversationId) => {
    e.stopPropagation();
    if (editTitle.trim() && onRenameConversation) {
      onRenameConversation(conversationId, editTitle.trim());
    }
    setEditingChat(null);
    setEditTitle('');
  };

  const handleCancelRename = (e) => {
    e.stopPropagation();
    setEditingChat(null);
    setEditTitle('');
  };

  const handleEditKeyDown = (e, conversationId) => {
    if (e.key === 'Enter') handleConfirmRename(e, conversationId);
    else if (e.key === 'Escape') handleCancelRename(e);
  };

  const ActionIconButton = ({ children, onClick, color = 'default', className, ...props }) => (
    <IconButton
      onClick={onClick}
      size="small"
      className={className}
      sx={{
        width: 28,
        height: 28,
        backgroundColor: 'rgba(255, 255, 255, 0.9)',
        backdropFilter: 'blur(4px)',
        boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
        color: color === 'danger' ? colors.accent : color === 'edit' ? colors.gray[500] : colors.gray[500],
        padding: '4px',
        transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
        '&:hover': {
          backgroundColor: color === 'danger'
            ? 'rgba(193, 92, 84, 0.1)'
            : color === 'edit'
              ? 'rgba(243, 216, 75, 0.15)'
              : 'rgba(0,0,0,0.05)',
          color: color === 'danger' ? colors.accent : color === 'edit' ? colors.secondary : colors.primary,
          transform: 'scale(1)',
          boxShadow: '0 2px 6px rgba(0,0,0,0.12)',
        },
        '&.always-visible': {
          opacity: 1,
          transform: 'translateX(0) scale(1)',
        },
      }}
      {...props}
    >
      {children}
    </IconButton>
  );

  return (
    <Box
      sx={{
        width: sidebarWidth,
        minWidth: sidebarWidth,
        maxWidth: sidebarWidth,
        height: '100vh',
        background: `linear-gradient(180deg, ${colors.background} 0%, #f9f0d9 100%)`,
        borderRight: isMobile ? 'none' : `1px solid ${colors.gray[200]}`,
        display: 'flex',
        flexDirection: 'column',
        transition: isMobile ? 'none' : 'width 0.3s cubic-bezier(0.4, 0, 0.2, 1), min-width 0.3s cubic-bezier(0.4, 0, 0.2, 1), max-width 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        overflow: 'hidden',
        borderRadius: 0,
        position: 'relative',
        flexShrink: 0,
      }}
    >
      {/* 顶部操作区 */}
      <Box
        sx={{
          p: isMobile ? 2 : 2,
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          justifyContent: 'space-between',
        }}
      >
        {isMobile && (
          <IconButton
            onClick={onCloseMobile}
            sx={{
              color: colors.primary,
              p: 1,
              borderRadius: 2,
              '&:hover': { backgroundColor: 'rgba(228, 116, 112, 0.08)' },
            }}
          >
            <ArrowBackIcon />
          </IconButton>
        )}

        <Box sx={{ flex: 1, ml: 1 }}>
          <TextField
            fullWidth
            placeholder="搜索对话..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            size="small"
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: 3,
                backgroundColor: 'rgba(255,255,255,0.8)',
                '& fieldset': { borderColor: 'transparent' },
                '&:hover fieldset': { borderColor: colors.secondary },
                '&.Mui-focused fieldset': { borderColor: colors.primary },
                '&.Mui-focused': { backgroundColor: '#fff' },
                transition: 'all 0.2s ease',
              }
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: colors.gray[400], fontSize: 18 }} />
                </InputAdornment>
              )
            }}
          />
        </Box>

        <Tooltip title="新建对话" placement="bottom">
          <IconButton
            onClick={onNewChat}
            sx={{
              p: isMobile ? 1 : 0.75,
              borderRadius: 2,
              backgroundColor: colors.primary,
              color: '#fff',
              '&:hover': {
                backgroundColor: colors.accent,
                transform: 'scale(1.05)'
              },
              transition: 'all 0.2s ease',
              flexShrink: 0,
              boxShadow: '0 2px 8px rgba(228, 116, 112, 0.3)',
            }}
          >
            <AddIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>

      {/* 聊天列表 */}
      <Box
        sx={{
          flex: 1,
          overflowY: 'auto',
          overflowX: 'hidden',
          py: 1,
          px: isMobile ? 1 : 1.5,
          '&::-webkit-scrollbar': { width: isMobile ? 3 : 5 },
          '&::-webkit-scrollbar-track': { background: 'transparent' },
          '&::-webkit-scrollbar-thumb': {
            background: `${colors.primary}40`,
            borderRadius: 3,
            '&:hover': { background: `${colors.primary}60` }
          },
          scrollbarWidth: 'thin',
          scrollbarColor: `${colors.primary}40 transparent`,
        }}
      >
        {(['今天', '昨天', '本周', '本月', '更早']
          .filter(group => filteredConversations[group])
          .map((group) => {
            const chats = filteredConversations[group];
            return (
          <Box key={group} sx={{ mb: isMobile ? 1 : 1.5 }}>
            <Typography
              variant="caption"
              sx={{
                px: 1.5,
                py: 0.5,
                color: colors.gray[400],
                fontWeight: 600,
                fontSize: '0.7rem',
                letterSpacing: '0.03em',
                display: 'block',
              }}
            >
              {group}
            </Typography>

            <List sx={{ py: 0 }}>
              {chats.map((conversation) => {
                const isSelected = selectedConversation && selectedConversation._id === conversation._id;
                const isHovered = hoveredChat === conversation._id;
                const isEditing = editingChat === conversation._id;
                const isConfirmingDelete = showDeleteConfirm === conversation._id;
                const showActions = isHovered || isEditing || isConfirmingDelete || isMobile;

                return (
                  <Tooltip key={conversation._id} title={isMobile ? "" : ""} placement="right" enterDelay={500}>
                    <ListItem
                      button
                      selected={isSelected}
                      onClick={() => onConversationSelect(conversation)}
                      onMouseEnter={() => setHoveredChat(conversation._id)}
                      onMouseLeave={() => setHoveredChat(null)}
                      sx={{
                        px: 1.5,
                        py: 0,
                        mb: 0.25,
                        mx: 0.5,
                        borderRadius: '12px',
                        transition: 'background-color 0.2s ease, border-color 0.2s ease',
                        position: 'relative',
                        cursor: 'pointer',
                        minHeight: isMobile ? 'auto' : 48,
                        height: isMobile ? 'auto' : 48,
                        backgroundColor: isSelected
                          ? 'rgba(228, 116, 112, 0.12)'
                          : isHovered
                            ? 'rgba(243, 216, 75, 0.12)'
                            : 'transparent',
                        border: '1px solid transparent',
                        '&:hover': {
                          backgroundColor: isSelected
                            ? 'rgba(228, 116, 112, 0.16)'
                            : 'rgba(243, 216, 75, 0.16)',
                          border: isSelected
                            ? `1px solid rgba(228, 116, 112, 0.2)`
                            : `1px solid rgba(243, 216, 75, 0.2)`,
                        },
                        '&::before': isSelected ? {
                          content: '""',
                          position: 'absolute',
                          left: 0,
                          top: '20%',
                          bottom: '20%',
                          width: 3,
                          borderRadius: '0 3px 3px 0',
                          backgroundColor: colors.primary,
                        } : {},
                        '&::after': conversation.unread && !showActions ? {
                          content: '""',
                          position: 'absolute',
                          right: 12,
                          top: '50%',
                          transform: 'translateY(-50%)',
                          width: 7,
                          height: 7,
                          borderRadius: '50%',
                          backgroundColor: colors.accent,
                          boxShadow: `0 0 0 2px ${colors.background}`,
                        } : {},
                      }}
                    >
                      <Box sx={{
                        flex: 1,
                        minWidth: 0,
                        pr: 0,
                      }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1 }}>
                          {isEditing ? (
                            <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', gap: 0.5 }} onClick={(e) => e.stopPropagation()}>
                              <TextField
                                value={editTitle}
                                onChange={(e) => setEditTitle(e.target.value)}
                                onKeyDown={(e) => handleEditKeyDown(e, conversation._id)}
                                size="small"
                                autoFocus
                                fullWidth
                                sx={{
                                  '& .MuiOutlinedInput-root': {
                                    fontSize: '0.875rem',
                                    fontWeight: 500,
                                    borderRadius: 2,
                                    backgroundColor: '#fff',
                                  }
                                }}
                              />
                              <ActionIconButton onClick={(e) => handleConfirmRename(e, conversation._id)} color="primary" className="always-visible" sx={{ color: colors.primary, backgroundColor: `${colors.primary}15` }}>
                                <CheckIcon sx={{ fontSize: 16 }} />
                              </ActionIconButton>
                              <ActionIconButton onClick={handleCancelRename} className="always-visible">
                                <CloseIcon sx={{ fontSize: 16 }} />
                              </ActionIconButton>
                            </Box>
                          ) : isConfirmingDelete ? (
                            <Box sx={{
                              flex: 1,
                              display: 'flex',
                              alignItems: 'center',
                              gap: 1,
                              py: 0.5,
                            }} onClick={(e) => e.stopPropagation()}>
                              <Typography variant="body2" sx={{
                                color: colors.accent,
                                fontWeight: 600,
                                fontSize: '0.8rem',
                                flex: 1,
                              }}>
                                删除此对话?
                              </Typography>
                              <ActionIconButton onClick={(e) => confirmDelete(e, conversation._id)} color="danger" className="always-visible" sx={{ backgroundColor: `${colors.accent}15` }}>
                                <CheckIcon sx={{ fontSize: 16 }} />
                              </ActionIconButton>
                              <ActionIconButton onClick={cancelDelete} className="always-visible">
                                <CloseIcon sx={{ fontSize: 16 }} />
                              </ActionIconButton>
                            </Box>
                          ) : (
                            <>
                              <Box sx={{ flex: 1, minWidth: 0 }}>
                                <Typography
                                  variant="body2"
                                  sx={{
                                    fontWeight: isSelected ? 600 : 500,
                                    color: isSelected ? colors.primary : colors.text.primary,
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                    fontSize: isMobile ? '0.95rem' : '0.875rem',
                                    transition: 'color 0.2s ease',
                                    lineHeight: 1.4,
                                  }}
                                >
                                  {conversation.title}
                                </Typography>
                              </Box>

                              <Box sx={{
                                position: 'relative',
                                flexShrink: 0,
                                width: 72,
                                height: 28,
                              }}>
                                <Typography
                                  variant="caption"
                                  sx={{
                                    color: colors.gray[300],
                                    fontSize: '0.65rem',
                                    position: 'absolute',
                                    right: 0,
                                    top: '50%',
                                    transform: 'translateY(-50%)',
                                    opacity: showActions ? 0 : 1,
                                    transition: 'opacity 0.15s ease',
                                    pointerEvents: showActions ? 'none' : 'auto',
                                    whiteSpace: 'nowrap',
                                  }}
                                >
                                  {formatTime(conversation.updatedAt)}
                                </Typography>
                                <Box sx={{
                                  display: 'flex',
                                  gap: 0.5,
                                  position: 'absolute',
                                  right: 0,
                                  top: '50%',
                                  transform: 'translateY(-50%)',
                                  opacity: showActions ? 1 : 0,
                                  transition: 'opacity 0.15s ease, transform 0.15s ease',
                                  pointerEvents: showActions ? 'auto' : 'none',
                                }}>
                                  <ActionIconButton onClick={(e) => handleStartEdit(e, conversation)} color="edit">
                                    <EditIcon sx={{ fontSize: 15 }} />
                                  </ActionIconButton>
                                  <ActionIconButton onClick={(e) => handleDeleteClick(e, conversation)} color="danger">
                                    <DeleteIcon sx={{ fontSize: 15 }} />
                                  </ActionIconButton>
                                </Box>
                              </Box>
                            </>
                          )}
                        </Box>

                        {isMobile && !isEditing && !isConfirmingDelete && (
                          <Box sx={{
                            display: 'flex',
                            gap: 0.5,
                            mt: 1,
                            pt: 1,
                            borderTop: '1px solid rgba(0,0,0,0.04)',
                            justifyContent: 'flex-end'
                          }} onClick={(e) => e.stopPropagation()}>
                            <IconButton
                              size="small"
                              onClick={(e) => handleStartEdit(e, conversation)}
                              sx={{
                                color: colors.gray[500],
                                p: 0.75,
                                borderRadius: 1.5,
                                '&:hover': {
                                  color: colors.secondary,
                                  backgroundColor: 'rgba(243, 216, 75, 0.12)',
                                },
                                fontSize: '0.8rem',
                              }}
                            >
                              <EditIcon sx={{ fontSize: 16, mr: 0.5 }} />
                              重命名
                            </IconButton>
                            <IconButton
                              size="small"
                              onClick={(e) => handleDeleteClick(e, conversation)}
                              sx={{
                                color: colors.accent,
                                p: 0.75,
                                borderRadius: 1.5,
                                '&:hover': {
                                  backgroundColor: 'rgba(193, 92, 84, 0.1)',
                                },
                                fontSize: '0.8rem',
                              }}
                            >
                              <DeleteIcon sx={{ fontSize: 16, mr: 0.5 }} />
                              删除
                            </IconButton>
                          </Box>
                        )}
                      </Box>
                    </ListItem>
                  </Tooltip>
                );
              })}
            </List>
          </Box>
            );
          }))}

        {Object.keys(filteredConversations).length === 0 && (
          <Box sx={{ textAlign: 'center', py: isMobile ? 8 : 6, px: 2 }}>
            <Typography sx={{ fontSize: isMobile ? '3rem' : '2.5rem', mb: 2, opacity: 0.5 }}>
              💬
            </Typography>
            <Typography variant="body2" sx={{
              color: colors.gray[400],
              fontSize: isMobile ? '0.9rem' : '0.85rem',
              lineHeight: 1.6,
            }}>
              {searchTerm ? '没有找到相关对话' : '还没有对话'}
              <br />
              点击上方 + 按钮开始新对话
            </Typography>
          </Box>
        )}
      </Box>

      <Box sx={{
        minHeight: isMobile ? 68 : 64,
        maxHeight: isMobile ? 68 : 64,
        width: '100%',
        display: 'flex',
        alignItems: 'center',
        borderTop: `1px solid ${colors.gray[200]}`,
        backgroundColor: 'rgba(255,255,255,0.3)',
      }}>
        <AuthButton isMobile={isMobile} />
      </Box>
    </Box>
  );
};

export default Sidebar;
